import { AdminDataTable } from './AdminDataTable';
import { showDialog } from '@src/components/Dialog/Dialog';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PENDING_PREMIUM_BOX_KEY_PREFIX } from '@lub0v/santa-utils';
import { Button, Spinner, Hint } from '@src/components';
import { showToast } from '@src/components/Toast';
import { configuredBrowserBoxDomains } from '@src/routing/boxDomainNavigation';
import { CopyValue } from './AdminPayments';
import './AdminPayments.css';

type Domain = {
  id: number;
  name: string;
  key: string;
  premium_key: string;
  year: string;
  is_premium: boolean;
  is_archived: boolean;
  organizer_ids: number[];
  organizers: { id: number; email: string | null }[];
};
type DomainList = { domains: Domain[]; total: number; page: number; pageSize: number };

export function AdminDomains({
  onDenied,
  onChanged,
}: {
  onDenied: () => void;
  onChanged: () => void;
}) {
  const [revision, setRevision] = useState(0);
  return (
    <>
      <DomainTable
        filter="pending"
        revision={revision}
        onApproved={() => {
          setRevision((n) => n + 1);
          onChanged();
        }}
        onDenied={onDenied}
      />
      <DomainTable
        filter="approved"
        revision={revision}
        onApproved={() => {
          setRevision((n) => n + 1);
          onChanged();
        }}
        onDenied={onDenied}
      />
    </>
  );
}

function DomainTable({
  filter,
  revision,
  onApproved,
  onDenied,
}: {
  filter: 'pending' | 'approved';
  revision: number;
  onApproved: () => void;
  onDenied: () => void;
}) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<DomainList | null>(null);
  const [error, setError] = useState(false);
  const [approving, setApproving] = useState<number | null>(null);
  const hostname = window.location.hostname.replace(/^www\./, '');
  const local = ['localhost', '127.0.0.1', '[::1]'].includes(hostname);
  const domainBase =
    configuredBrowserBoxDomains().find(
      (candidate) =>
        hostname === candidate ||
        hostname.endsWith(`.${candidate}`) ||
        (local && candidate.endsWith('.localhost')),
    ) || hostname;
  const base = `${domainBase}${window.location.port ? `:${window.location.port}` : ''}`;
  useEffect(() => {
    const controller = new AbortController();
    let busy = false;
    async function load() {
      if (busy) return;
      busy = true;
      try {
        const response = await fetch(`/api/admin/domains?filter=${filter}&page=${page}`, {
          credentials: 'same-origin',
          cache: 'no-store',
          signal: controller.signal,
        });
        if ([401, 403].includes(response.status)) {
          onDenied();
          return;
        }
        if (!response.ok) throw new Error('Domains unavailable');
        const data: DomainList = await response.json();
        if (!Array.isArray(data.domains)) throw new Error('Invalid domain list');
        if (!controller.signal.aborted) {
          setResult(data);
          setPage(data.page);
          setError(false);
        }
      } catch {
        if (!controller.signal.aborted) setError(true);
      } finally {
        busy = false;
      }
    }
    void load();
    const timer = window.setInterval(() => void load(), 60_000);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
    // These callbacks only change authorization or request another refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, page, revision]);
  async function approve(domain: Domain) {
    if (approving !== null) return;
    setApproving(domain.id);
    try {
      const response = await fetch(`/api/admin/domains/${domain.id}/approve`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expectedKey: domain.premium_key }),
      });
      if ([401, 403].includes(response.status)) {
        onDenied();
        return;
      }
      if (!response.ok) throw new Error('Approval failed');
      onApproved();
      showToast(t('admin.domains.approvedMessage'), 'success');
    } catch (error) {
      showToast(t('admin.domains.approveError'));
      throw error;
    } finally {
      setApproving(null);
    }
  }
  return (
    <section className="admin-payments" aria-label={t(`admin.domains.${filter}`)}>
      <h2>{t(`admin.domains.${filter}`)}</h2>
      <p>{t('admin.payments.refreshNote')}</p>
      {error && <Hint tone="error">{t('admin.domains.error')}</Hint>}
      {!result && !error && <Spinner label={t('account.loading')} />}
      {result && (
        <div>
          <div
            className="admin-payments__scroll"
            role="region"
            aria-label={t(`admin.domains.${filter}`)}
            tabIndex={0}
          >
            <AdminDataTable data-testid={`admin-domains-${filter}-table`} pagination={{
            testId: `admin-domains-${filter}-table-pages`,
            page,
            pageCount: Math.max(1, Math.ceil(result.total / result.pageSize)),
            onChange: (next) => {
              setResult(null);
              setPage(next);
            },
            previousLabel: t('admin.payments.previous'),
            nextLabel: t('admin.payments.next'),
            pageLabel: (value) => t('admin.payments.page', { page: value }),
          }} className="admin-domains__table">
              <thead>
                <tr>
                  {['box', 'boxName', 'domain', 'organizers', 'actions'].map((key) => (
                    <th key={key} scope="col">
                      {t(`admin.domains.${key}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.domains.map((domain) => {
                  const name = domain.premium_key.startsWith(PENDING_PREMIUM_BOX_KEY_PREFIX)
                    ? domain.premium_key.slice(PENDING_PREMIUM_BOX_KEY_PREFIX.length)
                    : domain.premium_key;
                  return (
                    <tr key={domain.id}>
                      <td>
                        <CopyValue
                          value={String(domain.id)}
                          label={t('admin.domains.copyBoxId', { id: domain.id })}
                        />
                      </td>
                      <td className="admin-domains__box-name">
                        <strong>{domain.name}</strong>
                        <small>{domain.year}</small>
                      </td>
                      <td>
                        <strong>
                          {name}.{base}
                        </strong>
                      </td>
                      <td className="admin-domains__organizers">
                        {domain.organizers.length
                          ? domain.organizers.map((organizer) => (
                              <div key={organizer.id} className="admin-domains__organizer">
                                {organizer.email ? (
                                  <CopyValue
                                    value={organizer.email}
                                    label={t('admin.payments.copyEmail', {
                                      value: organizer.email,
                                    })}
                                  />
                                ) : (
                                  <small>{t('admin.domains.noEmail')}</small>
                                )}
                              </div>
                            ))
                          : '—'}
                      </td>
                      <td className="admin-payments__actions-cell">
                        {filter === 'pending' ? (
                          <Button
                            size="small"
                            variant="secondary"
                            loading={approving === domain.id}
                            disabled={
                              approving !== null || !domain.is_premium || domain.is_archived
                            }
                            onClick={() =>
                              showDialog({
                                title: t('admin.domains.confirmTitle'),
                                children: (
                                  <p>
                                    {t('admin.domains.confirmBody', {
                                      domain: `${domain.premium_key.slice(PENDING_PREMIUM_BOX_KEY_PREFIX.length)}.${base}`,
                                      box: domain.name,
                                      id: domain.id,
                                    })}
                                  </p>
                                ),
                                cancel: t('admin.confirmCancel'),
                                submit: t('admin.domains.approve'),
                                onSubmit: () => approve(domain),
                                testId: 'admin-confirm-domain',
                              })
                            }
                          >
                            {t('admin.domains.approve')}
                          </Button>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!result.domains.length && (
                  <tr>
                    <td colSpan={5}>{t('admin.domains.empty')}</td>
                  </tr>
                )}
              </tbody>
            </AdminDataTable>
          </div>
        </div>
      )}
    </section>
  );
}
